import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { v4 } from 'uuid';
import { Helmet } from 'react-helmet';
import { DB } from '~/services/firebase';
import { useAuth } from '~/hooks/auth';

import getValidationErrors from '~/utils/getValidationErrors';

import { useToast } from '~/hooks/toast';
import { useClients } from '~/hooks/clients';
import FormAddress, { FormAddressPropsRef } from '~/components/FormAddress';
import IClient from '~/interfaces/IClient';
import { getPhoneFormatted } from '~/utils/getPhoneFormatted';

interface FormData {
  uid: string;
  name: string;
  email: string;
  whatsapp: string;
  birthdate: string;
  status: boolean;
}

interface ClientFormDataProps {
  id?: string;
  name?: string;
  phoneNumber?: string;
  redirect?: string;
  onClose?: () => void;
}

export function ClientFormData({
  id,
  name,
  phoneNumber,
  redirect,
  onClose,
}: ClientFormDataProps): JSX.Element {
  const history = useHistory();

  const { user, company } = useAuth();
  const { addToast } = useToast();
  const [data, setData] = useState({} as FormData);
  const [errors, setErrors] = useState({} as any);
  const [loading, setLoading] = useState(false);
  const [updateScript, setUpdateScript] = useState(false);

  const addressRef = useRef<FormAddressPropsRef>(null);

  const { addClient, updateClient } = useClients();

  useEffect(() => {
    async function load() {
      const docSnap = await getDoc(doc(DB, 'clients', String(id)));
      const clientData = {
        ...docSnap.data(),
        uid: id,
      } as IClient;

      setData({
        ...docSnap.data(),
        uid: id,
      } as FormData);

      addressRef.current?.set({
        zipcode: String(clientData.zipcode || ''),
        city: String(clientData.city || ''),
        state: String(clientData.state || ''),
        street: String(clientData.street || ''),
        neighborhood: String(clientData.neighborhood || ''),
        number: String(clientData.number || ''),
        complement: String(clientData.complement || ''),
      });

      setTimeout(() => setUpdateScript(true), 100);
    }

    if (id) {
      load();
    } else {
      setData({
        name: name || '',
        whatsapp: getPhoneFormatted(String(phoneNumber)) || '',
        status: true,
        email: '',
        birthdate: '',
      } as FormData);
      setTimeout(() => setUpdateScript(true), 100);
    }
  }, [id, name, phoneNumber]);

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      try {
        setErrors({});

        const schema = Yup.object().shape({
          name: Yup.string().required('O nome do cliente é obrigatória'),
          whatsapp: Yup.string().required('O whatsapp é obrigatório'),
        });

        const addressFormData = await addressRef.current?.submit();

        await schema.validate(data, {
          abortEarly: false,
        });

        if (!addressFormData) return;

        setLoading(true);

        const { name: dataName, status, whatsapp, email, birthdate } = data;

        const uid = data.uid || v4();

        const parseData: any = {
          uid,
          name: dataName,
          email: email || '',
          whatsapp,
          status,
          birthdate: birthdate || '',
          user_id: user.uid,
          company_id: company.uid,
          ...addressFormData,
        };

        if (data.uid) {
          await updateDoc(doc(DB, 'clients', uid), parseData);
          updateClient(parseData);
        } else {
          await setDoc(doc(DB, 'clients', uid), parseData);
          addClient(parseData);
        }

        addToast({
          type: 'success',
          description: data.uid
            ? 'Cliente alterado com sucesso!'
            : 'Cliente cadastrado com sucesso!',
        });

        if (onClose) onClose();
        if (redirect) history.push(redirect);

        setLoading(false);
      } catch (err: any) {
        console.log(err);
        setLoading(false);
        if (err instanceof Yup.ValidationError) {
          const parseErrors = getValidationErrors(err);
          setErrors(parseErrors);
        }
      }
    },
    [
      user,
      company,
      data,
      addToast,
      history,
      addClient,
      updateClient,
      redirect,
      onClose,
    ],
  );

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="form-label">Nome do Cliente *</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control"
                  defaultValue={data.name}
                  onChange={event => {
                    setData({ ...data, name: event.target.value });
                  }}
                />
                {errors.name && <span className="invalid">{errors.name}</span>}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="form-label">Whatsapp *</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control phone"
                  defaultValue={data.whatsapp}
                  onChange={event => {
                    setData({
                      ...data,
                      whatsapp: event.target.value,
                    });
                  }}
                />
                {errors.whatsapp && (
                  <span className="invalid">{errors.whatsapp}</span>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="form-label">E-mail</label>
              <div className="form-control-wrap">
                <input
                  type="text"
                  className="form-control"
                  defaultValue={data.email}
                  onChange={event => {
                    setData({ ...data, email: event.target.value });
                  }}
                />
                {errors.email && (
                  <span className="invalid">{errors.email}</span>
                )}
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="form-label">Data de Nascimento</label>
              <div className="form-control-wrap">
                <input
                  type="date"
                  className="form-control"
                  defaultValue={data.birthdate}
                  onChange={event => {
                    setData({
                      ...data,
                      birthdate: event.target.value,
                    });
                  }}
                />
                {errors.birthdate && (
                  <span className="invalid">{errors.birthdate}</span>
                )}
              </div>
            </div>
          </div>

          <FormAddress ref={addressRef} />

          <div className="col-lg-6">
            <div className="form-group mb-3">
              <label className="form-label">Status do cliente?</label>
              <ul className="custom-control-group g-3 align-center">
                <li>
                  <div className="custom-control custom-control-sm custom-checkbox">
                    <input
                      id="active"
                      type="checkbox"
                      className="custom-control-input"
                      defaultChecked={data.status}
                      onClick={() => {
                        setData({
                          ...data,
                          status: !data.status,
                        });
                      }}
                    />
                    <label className="custom-control-label" htmlFor="active">
                      Ativo
                    </label>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-12">
            <div className="form-group">
              <button
                type="submit"
                className="btn btn-lg btn-primary"
                disabled={loading}
                onClick={handleSubmit}
              >
                {loading && (
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  />
                )}
                {!loading && 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </form>
      {updateScript && (
        <Helmet>
          <script src={`${process.env.PUBLIC_URL}/assets/js/masks.js`} />
        </Helmet>
      )}
    </>
  );
}
